import {
  Select,
  ListBox,
  ListBoxItem,
  Popover,
  Button,
  SelectValue,
  Label,
} from 'react-aria-components';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

type Item = {
  label: string;
  value: string;
};

type Props<T extends FieldValues> = {
  name: FieldPath<T>;
  label?: string;
  control: Control<T>;
  items: Item[];
  required?: boolean;
  defaultValue?: string;
  defaultValueLabel?: string;
  disabled?: boolean;
  className?: string;
};

const SelectField = <T extends FieldValues>(props: Props<T>) => {
  return (
    <div className={`relative w-full ${props.className || ''}`}>
      <Controller
        name={props.name}
        control={props.control}
        rules={{ required: props.required }}
        render={({ field: { onChange, value } }) => (
          <Select
            aria-label={`${props.label}-select`}
            id="select"
            selectedKey={value}
            onSelectionChange={(selected) => {
              if (typeof selected === 'string') {
                onChange(selected);
              }
            }}
            defaultSelectedKey={props.defaultValue}
            isDisabled={props.disabled}
          >
            {props.label && (
              <div className="flex flex-row items-center">
                <Label className="mb-2 text-base md:text-lg md:font-bold">{props.label}</Label>
                {props.required && <span className="mb-2 ml-1 text-sm text-red-800">※必須</span>}
              </div>
            )}
            <Button
              className={({ isDisabled }) =>
                `mb-2 flex w-full flex-row items-center justify-between rounded-md border border-gray-900 px-3 py-2 text-left text-sm ${
                  isDisabled
                    ? 'cursor-not-allowed border-gray-300 bg-gray-100 text-gray-500'
                    : 'bg-white focus:border-2 focus:border-yellow-700 focus:outline-none focus:ring-1 focus:ring-blue-500'
                } `
              }
            >
              <SelectValue className="block max-w-full truncate">
                {value
                  ? props.items.find((item) => item.value === value.toString())?.label
                  : props.defaultValueLabel || '選択してください'}
              </SelectValue>
              <span className={`pointer-events-none ml-2 ${props.disabled ? 'text-gray-400' : ''}`}>
                ▼
              </span>
            </Button>
            <Popover
              shouldFlip={true}
              placement="bottom start"
              className="w-full md:w-auto"
            >
              <ListBox className="max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white py-1 text-sm shadow-lg">
                {props.items.map((item, index) => (
                  <ListBoxItem
                    id={item.value}
                    key={index}
                    textValue={item.value}
                    className={({ isFocused, isSelected }) =>
                      `relative cursor-pointer select-none px-4 py-2 ${isFocused ? 'bg-blue-100' : ''} ${isSelected ? 'bg-blue-500 text-white' : ''} ${!isSelected && !isFocused ? 'text-gray-900' : ''} `
                    }
                  >
                    {item.label}
                  </ListBoxItem>
                ))}
              </ListBox>
            </Popover>
          </Select>
        )}
      />
    </div>
  );
};

export default SelectField;
